/**
 * Hook to fetch images that require authentication
 * Fetches the image with auth headers and returns a blob URL
 */

import { useState, useEffect } from 'react';
import { servicesContainer } from '@/core/di/services-container';

interface UseAuthenticatedImageResult {
  blobUrl: string | null;
  isLoading: boolean;
  error: string | null;
}

/**
 * Fetches an image URL using authentication and returns a blob URL
 * Use this when S3 URLs require auth headers that can't be passed via <img src>
 * 
 * @param url - The authenticated image URL to fetch
 * @returns Object with blobUrl, isLoading, and error states
 */
export function useAuthenticatedImage(url: string | null | undefined): UseAuthenticatedImageResult {
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Reset state when URL changes
    setBlobUrl(null);
    setError(null);

    if (!url) {
      setIsLoading(false);
      return;
    }

    // Check if URL is a data URL or blob URL (already accessible)
    if (url.startsWith('data:') || url.startsWith('blob:')) {
      setBlobUrl(url);
      setIsLoading(false);
      return;
    }

    // Check if URL is a local/relative URL (doesn't need auth)
    if (url.startsWith('/') && !url.startsWith('//')) {
      setBlobUrl(url);
      setIsLoading(false);
      return;
    }

    let isCancelled = false;

    const fetchImage = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const { tokenManager } = servicesContainer.getServices();
        const authHeaders = await tokenManager.getAuthHeaders();

        const response = await fetch(url, {
          method: 'GET',
          headers: {
            ...authHeaders,
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to load image: ${response.status} ${response.statusText}`);
        }

        const blob = await response.blob();
        
        if (!isCancelled) {
          const objectUrl = URL.createObjectURL(blob);
          setBlobUrl(objectUrl);
        }
      } catch (err) {
        if (!isCancelled) {
          console.error('Failed to fetch authenticated image:', err);
          setError(err instanceof Error ? err.message : 'Failed to load image');
          // Fallback: try using the URL directly (might work for public assets)
          setBlobUrl(url);
        }
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    };

    fetchImage();

    // Cleanup: revoke blob URL when component unmounts or URL changes
    return () => {
      isCancelled = true;
      if (blobUrl && blobUrl.startsWith('blob:')) {
        URL.revokeObjectURL(blobUrl);
      }
    };
  }, [url]);

  return { blobUrl, isLoading, error };
}
